import { Link, useSearchParams } from 'react-router';
import { useReportsList } from '@/hooks/use-reports';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { ReportApprovalStatus } from '@/types/reports';

const STATUS_LABELS: Record<string, string> = {
  all: 'All',
  not_required: 'Auto',
  pending: 'Pending',
  approved: 'Approved',
  rejected: 'Rejected',
};

function statusVariant(status: ReportApprovalStatus): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (status) {
    case 'pending':
      return 'default';
    case 'approved':
      return 'secondary';
    case 'rejected':
      return 'destructive';
    default:
      return 'outline';
  }
}

export default function ReportsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const status = (searchParams.get('status') ?? 'all') as ReportApprovalStatus | 'all';
  const page = Number(searchParams.get('page') ?? '1');
  const pageSize = 20;

  const { data, isLoading } = useReportsList({
    status: status === 'all' ? undefined : status,
    page,
    page_size: pageSize,
  });

  const updateParam = (key: string, value: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value === '' || value === 'all') {
        next.delete(key);
      } else {
        next.set(key, value);
      }
      if (key !== 'page') next.set('page', '1');
      return next;
    });
  };

  const total = data?.total ?? 0;
  const totalPages = total > 0 ? Math.ceil(total / pageSize) : 1;

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold">Seed Reports</h1>
        <p className="text-sm text-muted-foreground">
          Review student submissions for teacher-assigned practices.
        </p>
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Status</span>
          <Select value={status} onValueChange={(v) => updateParam('status', v ?? '')}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(STATUS_LABELS).map(([v, label]) => (
                <SelectItem key={v} value={v}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Student</TableHead>
              <TableHead>Practice</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Attachments</TableHead>
              <TableHead>Submitted</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={5}>
                  <Skeleton className="h-8 w-full" />
                </TableCell>
              </TableRow>
            )}
            {!isLoading && (data?.reports?.length ?? 0) === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="py-8 text-center text-sm text-muted-foreground">
                  No reports yet.
                </TableCell>
              </TableRow>
            )}
            {!isLoading &&
              data?.reports?.map((r) => (
                <TableRow key={r.id} className="cursor-pointer">
                  <TableCell>
                    <Link to={`/reports/${r.id}`} className="hover:underline">
                      {r.student_name || 'Student'}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Link to={`/reports/${r.id}`} className="hover:underline">
                      {r.seed_title}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Badge variant={statusVariant(r.approval_status)}>{r.approval_status}</Badge>
                  </TableCell>
                  <TableCell>{r.attachments?.length ?? 0}</TableCell>
                  <TableCell>{new Date(r.submitted_at).toLocaleString()}</TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground">
          {total > 0 ? `${total} report${total === 1 ? '' : 's'}` : ''}
        </span>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => updateParam('page', String(page - 1))}
          >
            Previous
          </Button>
          <span className="text-sm text-muted-foreground self-center">
            Page {page} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => updateParam('page', String(page + 1))}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}
